// angular
import { NgOptimizedImage, ViewportScroller } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnChanges,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import {
  CardPropertyComponent,
  ICardProperty,
} from '@app/core/components/card-property/card-property.component';
import { ContactDialogComponent } from '@app/core/dialogs/contact-dialog/contact-dialog.component';
import { GalleryComponent } from '@app/core/components/gallery/gallery.component';
import { SubmitRequestDialogComponent } from '@app/core/dialogs/submit-request-dialog/submit-request-dialog.component';
import { ItemPricesComponent } from '@app/core/components/item-prices/item-prices.component';
// services
import { DialogService } from '@core/shared/services/dialog.service';
import { UserService } from '@app/shared/services/user.service';
import { StateManagementService } from '@app/+chat/shared/services/state-management.service';
import { ChatService } from '@app/+chat/shared/services/chat.service';
import { AlertStatesEnum } from '@app/+chat/shared/enums/alert-states.enum';
import { CurrencyService } from '@app/core/shared/services/currency.service';
import { StorageService } from '@app/shared/services/storage.service';
// types & interfaces & env
import { ContactDialogItem } from '@app/core/dialogs/contact-dialog/shared/contact-dialog-item.type';
import { ProductPreviewType } from '@app/+products/shared/types/product-preview.type';
import { IMedia } from '@app/shared/interfaces/media.interface';
import { PriceType } from '@app/+products/shared/interfaces/product.interface';
import { environment } from '@environments/environment';
// pipes & enums
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
import { RegisterButtonComponent } from '@app/core/components/register-button/register-button.component';
import {MatomoTrackClickDirective} from "ngx-matomo-client";

@Component({
  selector: 'pwj-product-preview',
  standalone: true,
  imports: [
    NgOptimizedImage,
    ButtonComponent,
    CardPropertyComponent,
    TranslateModule,
    TranslateDataPipe,
    GalleryComponent,
    ItemPricesComponent,
    RegisterButtonComponent,
    MatomoTrackClickDirective,
  ],
  templateUrl: './product-preview.component.html',
  styleUrl: './product-preview.component.scss',
})
export class ProductPreviewComponent implements OnChanges {
  public panelUrl: string = environment.baseUrl.panelUrl;
  public baseUrl: string = environment.baseUrl.base;
  public productProperties!: ICardProperty[];
  public selectedProductMedia!: IMedia;
  public contactData!: ContactDialogItem[];
  public userName?: string;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;
  public isUserLoggedIn!: boolean;

  // inputs
  previewData = input.required<ProductPreviewType>();

  readonly descriptionPreviewText = computed(() => {
    const text = (
      this._translateDataPipe.transform(this.previewData()?.description) ?? ''
    ).trim();
    return text === '-' ? '' : text;
  });

  readonly isSubscribed = computed(
    () => this._userService.userSignal()?.is_subscribed === true
  );

  // dependencies
  private readonly _translateService = inject(TranslateService);
  private readonly _translateDataPipe = inject(TranslateDataPipe);
  private readonly _dialogService = inject(DialogService);
  private readonly _viewportScroller = inject(ViewportScroller);
  private readonly _userService = inject(UserService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _currenciesService = inject(CurrencyService);
  private readonly _storageService = inject(StorageService);
  private readonly _stateManagement = inject(StateManagementService);
  private readonly _chatService = inject(ChatService);

  constructor() {
    // set current currency
    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;
    effect(() => {
      this.currentCurrency = this._currenciesService.currentCurrency();
    });

    // checked user logged-in or no
    effect(() => {
      this.isUserLoggedIn =
        (this._userService.userSignal()?.roles?.length || 0) > 0;
    });

    // get preview data on lang change
    this._translateService.onLangChange.subscribe(_ => {
      this.getPreviewData();
    });

    // set user business id
    effect(() => {
      this.userName = this._userService.userSignal()?.username;
      this._cdr.detectChanges();
    });
  }

  ngOnChanges(): void {
    // check product added to favorite or not
    this.getPreviewData();
  }

  public getPreviewData() {
    if (this.previewData()) {
      // Only show properties that have a non-null value
      const code = this.previewData()?.code;
      const category = this.previewData()?.category;
      const subCategory = this.previewData()?.sub_category;
      const stoneGrade = this.previewData()?.stone_grade;
      this.productProperties = [
        code != null && {
          name: this._translateService.instant('products.code'),
          icon: 'hashtag',
          value: String(code),
          iconClasses: 'text-gray-600',
          summarizeLength: 120,
        },
        category && {
          name: this._translateService.instant('products.form'),
          icon: '3d-cube',
          value: category,
          iconClasses: 'text-gray-600',
          isI18nValueType: true,
          summarizeLength: 120,
        },
        subCategory && {
          name: this._translateService.instant('products.category'),
          icon: 'grid-apps',
          value: subCategory,
          iconClasses: 'text-gray-600',
          isI18nValueType: true,
          summarizeLength: 120,
        },
        stoneGrade && {
          name: this._translateService.instant('products.grade'),
          icon: 'ranking-1',
          value: `enums.grades.${stoneGrade}`,
          valueClasses: 'text-primary-dark',
          iconClasses: 'text-gray-600',
          summarizeLength: 120,
        },
      ].filter(Boolean) as ICardProperty[];

      this.selectedProductMedia = this.previewData()?.media[0];

      // set contact data
      this.contactData = [
        {
          id: 1,
          description: 'contactUsDialog.chatOnWhatsApp',
          details: this.previewData()?.mobile,
          detailsClasses: 'is-number block',
          buttonIcon: 'phone',
          buttonText: 'contactUsDialog.whatsApp',
          link:
            this.previewData()?.mobile !== '-'
              ? `https://wa.me/${this.previewData()?.mobile}`
              : undefined,
        },
        {
          id: 2,
          description: 'contactUsDialog.voiceCallWithSupplier',
          details: this.previewData()?.mobile,
          detailsClasses: 'is-number block',
          buttonIcon: 'mobile',
          buttonText: 'contactUsDialog.call',
          link:
            this.previewData()?.mobile !== '-'
              ? `tell:${this.previewData()?.mobile}`
              : undefined,
        },
        {
          id: 3,
          description: 'contactUsDialog.chatWithSupplier',
          buttonIcon: 'message-dots',
          buttonText: 'contactUsDialog.chat',
          needAction: true,
          action: 'openChat',
        },
        {
          id: 4,
          description: 'contactUsDialog.meetingWithSupplier',
          buttonIcon: 'video',
          buttonText: 'contactUsDialog.meeting',
          link: 'https://calendar.google.com/calendar',
        },
      ];
    }
  }

  public onSelectMainImage(media: IMedia) {
    this.selectedProductMedia = media;
  }

  public contactUs() {
    const dialogRef = this._dialogService.open(ContactDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'global.contact',
        contactsData: this.contactData,
      },
    });

    dialogRef.afterClosed().subscribe(_ => {});
  }

  public submitARequest() {
    const dialogRef = this._dialogService.open(SubmitRequestDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'global.submitARequest',
        productId: this.previewData()?._id,
        productBusinessId: this.previewData()?.business_id,
      },
    });

    dialogRef.afterClosed().subscribe(_ => {});
  }

  public goToDescription() {
    this._viewportScroller.scrollToPosition([0, 800]);
  }

  public sendMessage() {
    this._openSupportChat();
    this._stateManagement.isChatBoxOpen.set(true);
  }

  private _openSupportChat() {
    if (!this.isUserLoggedIn) {
      this._stateManagement.chatAlertState.set(AlertStatesEnum.publicChat);
      return;
    }

    this._stateManagement.chatAlertState.set(AlertStatesEnum.supportChat);

    const chatId = this._storageService.getChatId();
    if (chatId) {
      this._chatService.getChatByChatId(chatId);
      return;
    }

    if (!this._storageService.getStartedSupportChat()) {
      this._storageService.setStartedSupportChat();
    }
    this._chatService.createSupportChat();
  }
}
